/**
 * YIN pitch detection — ported from Dan Tranh Tuner's `yin.js`.
 *
 * YIN beats naive autocorrelation on plucked-string signals because its
 * cumulative-mean-normalized-difference (CMNDF) step suppresses the
 * octave-ambiguity that autocorrelation reports when harmonics dominate
 * the fundamental. That's exactly the case for đàn tranh: metal-string
 * resonance is harmonic-rich and the 2nd/3rd harmonic can be louder
 * than F0 for the first few hundred ms.
 *
 * Reference: de Cheveigné & Kawahara (2002), "YIN, a fundamental
 * frequency estimator for speech and music."
 */

export class YinDetector {
  readonly sampleRate: number;
  readonly bufferSize: number;
  /** Probability threshold for confident detection. Lower = more
   *  sensitive, more false positives. 0.15 is the paper's recommended
   *  default; we keep it. */
  threshold: number;
  /** Reject peaks weaker than this when falling back to global min. */
  probabilityThreshold: number;

  // Pre-allocated scratch buffers — re-used every frame to keep the GC
  // quiet during the ~30 Hz detection loop.
  private yinBuf: Float32Array;
  private cmndf: Float32Array;

  constructor(sampleRate: number, bufferSize = 2048) {
    this.sampleRate = sampleRate;
    this.bufferSize = bufferSize;
    this.threshold = 0.15;
    this.probabilityThreshold = 0.1;
    const half = bufferSize >> 1;
    this.yinBuf = new Float32Array(half);
    this.cmndf = new Float32Array(half);
  }

  /** Returns detected frequency in Hz + the signal RMS (for downstream
   *  loudness-based rendering), or null if no confident pitch. */
  detect(buffer: Float32Array): { freq: number; confidence: number; rms: number } | null {
    if (buffer.length < this.bufferSize) return null;

    // RMS gate fully opened to 0.001 — captures the deepest part of
    // the release envelope. The earlier octave-flip risk at sub-
    // 0.003 levels came from threshold = 0.10 paired with low-SNR
    // CMNDF biasing; at threshold = 0.15 (set in the constructor)
    // the fundamental wins even on quiet samples, so we can keep
    // the gate barely above the noise floor of a typical mic. The
    // tuner's pitch trail then gets dots all the way down to a
    // near-silent dying string instead of cutting to null mid-decay.
    let rms = 0;
    for (let i = 0; i < buffer.length; i++) rms += buffer[i]! * buffer[i]!;
    rms = Math.sqrt(rms / buffer.length);
    if (rms < 0.001) return null;
    const signalRms = rms;

    const half = this.bufferSize >> 1;

    // Step 1 — difference function.
    for (let tau = 0; tau < half; tau++) {
      let sum = 0;
      for (let i = 0; i < half; i++) {
        const d = buffer[i]! - buffer[i + tau]!;
        sum += d * d;
      }
      this.yinBuf[tau] = sum;
    }

    // Step 2 — cumulative mean normalized difference (CMNDF).
    this.cmndf[0] = 1;
    let runningSum = 0;
    for (let tau = 1; tau < half; tau++) {
      runningSum += this.yinBuf[tau]!;
      this.cmndf[tau] = this.yinBuf[tau]! / (runningSum / tau);
    }

    // Step 3 — absolute threshold. Start at tau=2 to avoid octave errors.
    let tau = -1;
    for (let t = 2; t < half; t++) {
      if (this.cmndf[t]! < this.threshold) {
        // Found a dip; descend to the local minimum.
        while (t + 1 < half && this.cmndf[t + 1]! < this.cmndf[t]!) t++;
        tau = t;
        break;
      }
    }

    if (tau === -1) {
      // No confident dip — try the global minimum, reject if still weak.
      let minTau = 2;
      let minVal = this.cmndf[2]!;
      for (let t = 3; t < half; t++) {
        if (this.cmndf[t]! < minVal) {
          minVal = this.cmndf[t]!;
          minTau = t;
        }
      }
      if (minVal > this.probabilityThreshold) return null;
      tau = minTau;
    }

    // Deliberately NO octave-disambiguation here. Two attempts —
    //   (1) "d[2τ] < 0.7·d[τ]" in the difference-function domain, and
    //   (2) "cmndf[2τ] < cmndf[τ]" in the CMNDF domain
    // — both made things worse in practice. Reason: at the TRUE
    // fundamental P, d[P] and d[2P] are BOTH near zero (the signal
    // matches itself at both 1 and 2 periods), and CMNDF normalizes
    // by mean-d over [1..τ] — which is roughly constant as τ grows —
    // so cmndf[P] ≈ cmndf[2P] too. Rules on either ratio false-fired
    // 15–30 % of frames due to noise, producing OCTAVE-DOWN errors
    // on correct readings that were worse than the occasional
    // octave-UP flip they were meant to fix. Downstream median-of-10
    // absorbs the remaining octave flips more cleanly than any
    // per-frame correction we've tried.

    // Step 4 — parabolic interpolation for sub-sample precision.
    let refined = tau;
    if (tau > 0 && tau < half - 1) {
      const s0 = this.cmndf[tau - 1]!;
      const s1 = this.cmndf[tau]!;
      const s2 = this.cmndf[tau + 1]!;
      const denom = 2 * (2 * s1 - s2 - s0);
      if (denom !== 0) refined = tau + (s2 - s0) / denom;
    }

    const freq = this.sampleRate / refined;
    if (freq < 30 || freq > 4000) return null;

    // Confidence: inverse of the CMNDF value at tau. Closer to 1 =
    // clearer pitch.
    const confidence = Math.max(0, Math.min(1, 1 - this.cmndf[tau]!));
    return { freq, confidence, rms: signalRms };
  }
}

/** Hz → { pitchClass, octave, cents } relative to the nearest equal-
 *  tempered semitone (A4 = 440). Mirrors the helper in pitchDetector.ts
 *  — duplicated instead of imported to keep the tuner feature self-
 *  contained and independently tree-shakable. */
export function freqToNote(freq: number): {
  pitchClass: string;
  octave: number;
  cents: number;
  midiFloat: number;
} {
  const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
  const midiFloat = 69 + 12 * Math.log2(freq / 440);
  const midi = Math.round(midiFloat);
  const cents = Math.round((midiFloat - midi) * 100);
  const pitchClass = NOTE_NAMES[((midi % 12) + 12) % 12]!;
  const octave = Math.floor(midi / 12) - 1;
  return { pitchClass, octave, cents, midiFloat };
}
